import React, { useState } from 'react';
import { journeyFor, PROMOTE_PHASES } from '../../data/governanceJourney';
import { archetypeShortLabel } from '../../data/agentArchetypes';
import TrustWheel from './TrustWheel';
import {
  BODY, CAPTION, CAPTION_STRONG, EYEBROW, GLYPH, MONO, NOTE, PAGE_TITLE, PANEL_TITLE,
  SECTION_TITLE,
} from './typeScale';

/**
 * The ask, and the record of what has already been answered, side by side.
 *
 * <h3>What this is</h3>
 *
 * <p>Two columns from the large breakpoint up, one column below it. On the left, the single
 * thing being asked, with whatever answers it and whatever evidence that answer needs. On the
 * right, every answer already given, grouped by the stage it was given in, held in place while
 * the left column scrolls. Above both, one sentence saying whether this agent may move on and
 * what is stopping it.</p>
 *
 * <h3>What it replaced, and why the reason changed</h3>
 *
 * <p>The question used to be a node inside the record, and that was deliberate: three columns
 * had put the question in one and the evidence for it in another, so a paused build showed
 * "Working on it" in the centre while the answer sat in the next column as red text. Folding
 * them into one spine fixed that, and nothing here undoes it — the question still carries its
 * own evidence, in the panel with it.</p>
 *
 * <p>What moved out is the RECORD, which is a different thing from evidence: fourteen answers
 * above the question put the question below the fold on the screen whose only job is to ask
 * it. Measured on one agent: the question sat under nine cleared nodes.</p>
 *
 * <h3>Why the layout is in classes and the rest is not</h3>
 *
 * <p>An inline style cannot express a breakpoint. That single fact is why this surface had no
 * responsive behaviour at all while every other page in the console does, and it is why the
 * grid, the span and the sticky column are written in the console's own utilities. Colour and
 * type stay on the p7 tokens, which is this surface's language and not something a breakpoint
 * has an opinion about.</p>
 *
 * @param {Array}  feed     completed steps `[{id, at, title, text, detail[], tone, score}]`
 * @param {Object} ask      the step needing them `{number, question, hint, icon, stage}`, or null
 * @param {Object} verdict  as `verdictFromGate` returns it, or null for no band
 * @param {Node}   children whatever answers the ask — rendered in the ask column
 */
export default function BuildTrail({
  request, feed = [], ask = null, status, children,
  name, kind, stage, onExit, onReopen, canReopen,
  /* The agent's lifecycle phase, as the platform reports it. Null until it has been read — the
     Promote stage then says nothing about phases rather than guessing one. */
  phase = null,
  /* Null unless the gate has actually been read. A band that says "not ready" because a read
     failed is a claim about the agent made from a fact about the network. */
  verdict = null,
}) {
  const done = ['completed', 'failed', 'cancelled', 'rejected'].includes(status);
  /* Lowercased because the platform spells it both ways — the estate row title-cases it for
     display, the gate returns it lowercase. Anything outside these four is a value this does not
     know (`pending_verification` is one), and an unknown phase is reported as no phase. */
  const atPhase = PROMOTE_PHASES
    .find((pName) => pName.toLowerCase() === String(phase || '').toLowerCase()) || null;
  const promoted = String(phase || '').toLowerCase() === 'complete';
  const stages = journeyFor(kind);
  const found = stages.findIndex((st) => st.id === stage);
  /* Before the platform has reported a stage there is still a journey, and it
     starts at the first one. Leaving this at -1 made every section "future",
     which reads as though nothing has begun. */
  const hereAt = found >= 0 ? found : 0;
  const knownStage = (id) => stages.some((st) => st.id === id);
  /* Answered, not answering: what they have already given. */
  const given = ask ? Math.max(0, ask.number - 1) : 0;
  /* Per-stage open/closed. Undefined means "follow the journey" — the current
     stage open, the rest folded — so the trail behaves correctly before anyone
     has clicked anything, and remembers them once they have. */
  const [opened, setOpened] = useState({});

  /* Resolve each node's stage once, carrying the last seen one forward. */
  const stagedFeed = (() => {
    let carried = stages[0]?.id;
    return feed.map((e) => {
      if (knownStage(e.stage)) carried = e.stage;
      return { ...e, resolvedStage: carried };
    });
  })();

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '1240px', margin: '0 auto' }}>
      {/* The header is what says this is the same screen, further down: what is
          being protected, what kind of thing it is, how far along, and a way
          out. Without it the surface reads as a page that opened on top. */}
      {name ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--p7-space-3)',
                      flexWrap: 'wrap', marginBottom: 'var(--p7-space-2)' }}>
          <h1 style={PAGE_TITLE}>{name}</h1>
          {kind ? <span className="p7-status">{archetypeShortLabel(kind)}</span> : null}
          {onExit ? (
            <span style={{ marginLeft: 'auto' }}>
              <button type="button" className="p7-btn p7-btn--sm" onClick={onExit}>
                Save &amp; exit
              </button>
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Their own words for what is being brought under control. A line, not a
          card: in the approved direction the card above the fold is the ask. */}
      {request ? (
        <p style={{ ...BODY, color: 'var(--p7-ink-muted)', maxWidth: '78ch',
                    margin: '0 0 var(--p7-space-6)' }}>
          {request}
        </p>
      ) : null}

      {verdict ? <VerdictBand verdict={verdict} atPhase={atPhase} /> : null}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        {/* ── the one thing being asked ──────────────────────────────────── */}
        <section aria-label="Your part" className="min-w-0 lg:col-span-2">
          {ask ? (
            <AskPanel ask={ask}>{children}</AskPanel>
          ) : !done ? (
            <div className="p7-card" style={{ padding: 'var(--p7-space-5)' }}>
              <span style={EYEBROW}>Working</span>
              <p style={{ ...BODY, margin: 'var(--p7-space-2) 0 0' }}>
                Nothing changes until you have seen what it found.
              </p>
            </div>
          ) : null}
        </section>

        {/* ── the record ─────────────────────────────────────────────────── */}
        <section aria-label="The record"
                 className="p7-card min-w-0 lg:sticky lg:top-6"
                 style={{ padding: 'var(--p7-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline',
                        justifyContent: 'space-between', gap: 'var(--p7-space-3)',
                        marginBottom: 'var(--p7-space-3)' }}>
            <h2 style={PANEL_TITLE}>The record</h2>
            {/* No bar. A progress bar is a promise about the end expressed as geometry — it
                cannot be drawn without declaring how long the journey is, and that number
                moves whenever the journey gains a step. What somebody needs is how much they
                have given so far, which is true at every moment. */}
            {ask ? (
              <span style={CAPTION}>
                {given} {given === 1 ? 'answer' : 'answers'} from you
              </span>
            ) : null}
          </div>
          {/* ── The journey, one stage at a time ─────────────────────────
              Each section is the stage's own work. A completed stage folds to a
              single line, because once Identity is done the thing worth seeing
              is that it IS done, not the four lines proving it — they stay one
              click away.

              This replaced one undifferentiated list of nodes. Identity and
              Intent HAD run in that list, but with nothing naming them the
              journey read as a wall of sentences and those two stages looked
              like they had been skipped. */}
          <ul aria-label="Protection journey"
              style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex',
                       flexDirection: 'column', gap: 'var(--p7-space-2)' }}>
            {stages.map((st, idx) => {
              /* An untagged node sits with the work it arrived beside, not under
                 whatever stage happens to be current.
                 Two rules, learned the hard way:
                   - It must never VANISH. Filtering on an exact match alone
                     dropped untagged nodes off the trail entirely: the step
                     happened, the record said so, the screen showed nothing.
                   - It must never JUMP. Sending it to the current stage filed
                     "Bound to your connection" and the design-time score under
                     Access, three headings away from the work they belong to.
                 Inheriting the previous node's stage does both: narration is
                 emitted in order, so its neighbour is the best evidence
                 available, and anything before the first tagged node falls to
                 stage one. */
              const mine = stagedFeed.filter((e) => e.resolvedStage === st.id);
              /* Same rule as the nodes, and for the same reason: a question
                 whose stage we do not recognise is still a question being
                 asked, and it belongs where the journey currently is. */
              const asking = Boolean(ask) && (knownStage(ask.stage)
                ? ask.stage === st.id
                : hereAt === idx);
              const cleared = hereAt > idx || (hereAt === idx && !asking && mine.length > 0);
              const future = hereAt >= 0 && idx > hereAt;
              const open = asking || opened[st.id] === true
                           || (opened[st.id] === undefined && hereAt === idx);

              return (
                <li key={st.id}>
                  <StageSection
                    Icon={st.icon}
                    name={st.name}
                    /* Protect's own sentence for Promote, so the shared journey definition —
                       which asserts "promotion complete" unconditionally — is neither changed
                       nor printed over an agent still in a phase. */
                    definition={st.id === 'promote'
                      ? promoteDefinition(atPhase, promoted) : st.definition}
                    count={mine.length}
                    cleared={cleared}
                    current={hereAt === idx}
                    future={future}
                    open={open}
                    /* The stage being asked about stays open: folding away the
                       stage you are being asked about is never the intent. */
                    lockOpen={asking}
                    onToggle={() => setOpened((o) => ({ ...o, [st.id]: !open }))}
                  >
                    <ul aria-label={`${st.name} steps`}
                        style={{ listStyle: 'none', margin: 0, padding: 0,
                                 position: 'relative' }}>
                      {mine.map((e) => (
                        /* The step's own icon where the node IS one of the
                           console's steps; a tick where it is something the
                           platform did, which has no step and no icon there.
                           Never a tick on a held one: the ring turns amber, and
                           a tick inside it said done anyway — which is how a
                           step reporting an unmet gate read as a cleared one. */
                        <Node key={e.id} at={e.at} tone={e.tone}
                              Icon={e.icon}
                              marker={e.tone === 'hold' || e.tone === 'deny' ? '!' : '✓'}>
                          <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px',
                                         flexWrap: 'wrap' }}>
                            <span style={TITLE}>{e.title || e.text}</span>
                            {/* Not offered once the agent exists: everything at
                                or before the commit is frozen, because
                                answering the commit step again registers a
                                second agent. Whether a step is still open is
                                the sequence's answer, not this view's. */}
                            {e.stepKey && onReopen && (!canReopen || canReopen(e.stepKey)) ? (
                              <button type="button" className="p7-link"
                                      aria-label={`Change ${e.title || e.text}`}
                                      onClick={() => onReopen(e.stepKey)}
                                      style={{ border: 0, background: 'none', padding: 0,
                                               cursor: 'pointer', ...CAPTION,
                                               color: 'var(--p7-accent)' }}>
                                Change
                              </button>
                            ) : null}
                          </span>
                          {e.title && e.text ? <span style={MEANING}>{e.text}</span> : null}
                          {/* The score is the number this whole journey is for,
                              so it is drawn where it is earned rather than
                              written into a sentence that scrolls past. */}
                          {typeof e.score === 'number' ? (
                            <div style={{ marginTop: 'var(--p7-space-2)' }}>
                              <TrustWheel score={e.score} band={e.band}
                                          caption="Design-time score. It moves once it runs." />
                            </div>
                          ) : null}
                          {e.detail?.length ? (
                            <div data-testid="detail" style={DETAIL}>
                              {e.detail.map((d) => (
                                <div key={d} className="p7-mono"
                                     style={{ lineHeight: 1.7 }}>{d}</div>
                              ))}
                            </div>
                          ) : null}
                        </Node>
                      ))}
                    </ul>
                  </StageSection>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}

/**
 * The one thing being asked, in its own panel.
 *
 * <p>Raised and outlined in the accent, because on a screen with a record down one side and a
 * verdict across the top, the thing that needs a person has to be the thing the eye lands on.
 * Its evidence — the measures, the scores, the judge — renders inside it, which is what keeps
 * the question and the evidence for it together.</p>
 *
 * <p>The count says which question this is and not how many there are. A total cannot be
 * stated without promising how long the journey is, and the journey gains steps.</p>
 */
function AskPanel({ ask, children }) {
  const { icon: Icon } = ask;
  return (
    <section className="p7-card" aria-labelledby="protect-ask"
             style={{ borderColor: 'var(--p7-accent-line)',
                      boxShadow: 'var(--p7-elev-overlay)' }}>
      <div style={{ padding: 'var(--p7-space-5) var(--p7-space-5) 0' }}>
        <p style={{ ...EYEBROW, color: 'var(--p7-accent)', display: 'flex',
                    alignItems: 'center', gap: '6px', margin: 0 }}>
          {Icon ? <Icon size={13} strokeWidth={2.25} aria-hidden="true" /> : null}
          Your part · question {ask.number}
        </p>
        <h2 id="protect-ask"
            style={{ ...SECTION_TITLE, margin: 'var(--p7-space-2) 0 0' }}>
          {ask.question}
        </h2>
        {ask.hint ? (
          <p style={{ ...NOTE, marginTop: 'var(--p7-space-2)' }}>{ask.hint}</p>
        ) : null}
      </div>
      {children ? (
        <div style={{ padding: 'var(--p7-space-5)' }}>{children}</div>
      ) : (
        <div style={{ height: 'var(--p7-space-5)' }} />
      )}
    </section>
  );
}

/**
 * Whether this agent may move on, standing above everything else on the screen.
 *
 * <h3>Why it is here and not at the gate</h3>
 *
 * <p>The gate's own screen answers this when somebody goes and looks. The thing that was
 * missing is that an agent can be answered question after question while a condition it needs
 * sits unmet three screens away — which is how a policy measured at 57% held an agent in Build
 * through four more questions without the surface ever saying so.</p>
 *
 * <p>Every word of it is the gate's: the verdict is `allPassed`, the reason is the failing
 * check's own message. The count is the only thing added, because `allPassed` cannot carry
 * one. See {@code data/phaseGateChecks.js}.</p>
 */
function VerdictBand({ verdict, atPhase }) {
  const tone = verdict.ready ? 'allow' : 'hold';
  return (
    <section aria-label="Promotion readiness" className="p7-card"
             style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                      gap: 'var(--p7-space-3) var(--p7-space-5)',
                      borderLeft: `3px solid var(--p7-${tone})`,
                      padding: 'var(--p7-space-4) var(--p7-space-5)',
                      marginBottom: 'var(--p7-space-6)' }}>
      <span style={{ ...PANEL_TITLE, color: `var(--p7-${tone}-ink)`, whiteSpace: 'nowrap' }}>
        {verdict.state}
      </span>
      <span style={{ ...BODY, color: 'var(--p7-ink-muted)', flex: 1, minWidth: '240px' }}>
        {verdict.met} of {verdict.total} conditions met.
        {verdict.why ? <> <span style={{ color: 'var(--p7-ink)' }}>{verdict.why}</span></> : null}
      </span>
      <PhaseStrip atPhase={atPhase} />
    </section>
  );
}

/**
 * Which of the three promotion phases the agent is in.
 *
 * <p>Not the names of all three as a label — that is what the caption used to be, printed
 * whatever was true. `aria-current` marks the one it is in for a reader who cannot see the
 * weight.</p>
 */
function PhaseStrip({ atPhase }) {
  return (
    <span aria-label="Promotion phases"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', ...CAPTION }}>
      {PROMOTE_PHASES.map((pName, i) => (
        <React.Fragment key={pName}>
          {i > 0 ? <span aria-hidden="true" style={{ color: 'var(--p7-ink-faint)' }}>·</span> : null}
          <span
            aria-current={pName === atPhase ? 'step' : undefined}
            style={{ ...EYEBROW,
                     color: pName === atPhase ? 'var(--p7-ink)' : 'var(--p7-ink-faint)' }}>
            {pName}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * What the Promote stage says about where the agent is.
 *
 * <p>Only three things can be said truthfully: the agent is in a named phase, it is through all
 * of them, or the phase has not been read. This replaces a fixed sentence that said the third
 * case was the second — "Build · Decide · Act promotion complete." was printed over every agent,
 * including one still sitting in Build, from the moment the stage first rendered.</p>
 *
 * <p>An unrecognised value (the platform also reports things like `pending_verification` here)
 * is treated as not read. Guessing Build would be the same invention pointing the other way.</p>
 */
function promoteDefinition(atPhase, promoted) {
  if (promoted) return 'Build · Decide · Act promotion complete.';
  if (!atPhase) return 'Build, Decide and Act — each one gated before the next.';
  const ahead = PROMOTE_PHASES.slice(PROMOTE_PHASES.indexOf(atPhase) + 1);
  if (!ahead.length) return `In the ${atPhase} phase, the last before it runs.`;
  return `In the ${atPhase} phase. ${ahead.join(' and ')} ${ahead.length > 1 ? 'are' : 'is'} ahead.`;
}

/**
 * One stage of the journey: a header that is always readable, and a body that folds.
 *
 * <h3>What the header has to say on its own</h3>
 *
 * <p>A folded stage still has to answer "did this happen, and what was it". So the header
 * carries the stage's own icon — the same one the journey funnel draws it with, owned by
 * data/governanceJourney.js so the two screens cannot disagree — its name, its state, and how
 * many steps are inside. The stage's definition sits underneath while it is current or still to
 * come, and goes away once cleared, because by then it has been demonstrated rather than
 * described.</p>
 */
function StageSection({
  Icon, name, definition, count, cleared, current, future,
  open, lockOpen, onToggle, children,
}) {
  const tone = cleared ? 'var(--p7-allow, #16a34a)'
    : current ? 'var(--p7-accent)' : 'var(--p7-ink-faint, #94a3b8)';

  return (
    /* Labelled and current-marked on the SECTION, so "where is this agent"
       is answerable from the accessibility tree rather than from styling. */
    <section aria-label={name}
             aria-current={current ? 'step' : undefined}
             style={{ border: '1px solid var(--p7-line-soft)',
                      borderRadius: 'var(--p7-radius-button)',
                      background: current ? 'var(--p7-surface)' : 'transparent',
                      opacity: future ? 0.6 : 1 }}>
      <button type="button"
              onClick={lockOpen ? undefined : onToggle}
              aria-expanded={open}
              aria-disabled={lockOpen ? true : undefined}
              style={{ width: '100%', display: 'flex', alignItems: 'center',
                       gap: 'var(--p7-space-3)', textAlign: 'left',
                       padding: 'var(--p7-space-3)', border: 0, background: 'none',
                       cursor: lockOpen ? 'default' : 'pointer' }}>
        <span aria-hidden="true"
              style={{ flex: 'none', width: '22px', height: '22px', borderRadius: '50%',
                       display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                       border: `1.5px solid ${tone}`, color: tone, ...GLYPH }}>
          {Icon ? <Icon size={12} strokeWidth={2.25} aria-hidden="true" /> : null}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', ...CAPTION_STRONG,
                         color: current ? 'var(--p7-ink)' : 'var(--p7-ink-subtle)' }}>
            {name}
            {count ? (
              <span style={{ marginLeft: '8px', ...CAPTION,
                             color: 'var(--p7-ink-faint, #94a3b8)' }}>
                {count} step{count === 1 ? '' : 's'}
              </span>
            ) : null}
          </span>
          {!cleared && definition ? (
            <span style={{ display: 'block', marginTop: '1px', ...CAPTION,
                           color: 'var(--p7-ink-faint, #94a3b8)' }}>
              {definition}
            </span>
          ) : null}
        </span>
        {!lockOpen && (count > 0 || !future) ? (
          <span aria-hidden="true"
                style={{ flex: 'none', color: 'var(--p7-ink-faint, #94a3b8)',
                         transform: open ? 'rotate(90deg)' : 'none',
                         transition: 'transform .15s ease' }}>
            ›
          </span>
        ) : null}
      </button>
      {open ? (
        <div style={{ padding: '0 var(--p7-space-3) var(--p7-space-3)' }}>{children}</div>
      ) : null}
    </section>
  );
}

/** One step on the spine: the line, the marker, and what the step says. */
function Node({ at, marker, Icon, active = false, tone, children }) {
  return (
    <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start',
                 padding: '0 0 var(--p7-space-4)', position: 'relative' }}>
      <span style={{ flex: 'none', width: '30px', textAlign: 'right', ...MONO,
                     color: 'var(--p7-ink-faint)', lineHeight: '24px' }}>
        {at || ''}
      </span>
      <span style={{ flex: 'none', position: 'relative', width: '24px' }}>
        {/* The spine itself, drawn per node so it stops at the last one. */}
        <span aria-hidden="true"
              style={{ position: 'absolute', left: '11px', top: '24px', bottom: '-16px',
                       width: '2px', background: 'var(--p7-line)' }} />
        <span
          data-testid="marker"
          style={{
            position: 'relative', display: 'grid', placeItems: 'center',
            width: '24px', height: '24px', borderRadius: '50%', zIndex: 1, ...GLYPH,
            border: `2px solid ${active ? 'var(--p7-accent)' : tone === 'hold'
              ? 'var(--p7-hold)' : 'var(--p7-allow)'}`,
            background: active ? 'var(--p7-accent)' : tone === 'hold'
              ? 'var(--p7-hold-bg)' : 'var(--p7-allow-bg)',
            color: active ? 'var(--p7-ink-on-accent)' : tone === 'hold'
              ? 'var(--p7-hold-ink)' : 'var(--p7-allow-ink)',
          }}
        >
          {Icon ? <Icon size={12} strokeWidth={2.25} aria-hidden="true" /> : marker}
        </span>
      </span>
      <span style={{ minWidth: 0, flex: 1, paddingTop: '1px' }}>{children}</span>
    </li>
  );
}

const TITLE = { display: 'block', ...CAPTION_STRONG };
const MEANING = {
  display: 'block', marginTop: '2px', ...CAPTION,
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};
const DETAIL = {
  marginTop: '8px', paddingLeft: '12px', borderLeft: '2px solid var(--p7-line-soft)',
  ...CAPTION, color: 'var(--p7-ink-subtle)',
};
